import type { CSSProperties, ReactNode } from 'react';
import { resolveHudFrameToneColor, type HudFrameTone } from './hudDesign';

type HudFrameProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  tone?: HudFrameTone;
  dense?: boolean;
  children: ReactNode;
  style?: CSSProperties;
};

function cornerStyle(position: 'tl' | 'tr' | 'br' | 'bl', accent: string): CSSProperties {
  const base: CSSProperties = {
    position: 'absolute',
    width: 16,
    height: 16,
    pointerEvents: 'none',
    opacity: 0.95,
  };
  const borderColor = `${accent}AA`;

  if (position === 'tl') {
    return {
      ...base,
      top: 10,
      left: 10,
      borderTop: `2px solid ${borderColor}`,
      borderLeft: `2px solid ${borderColor}`,
    };
  }

  if (position === 'tr') {
    return {
      ...base,
      top: 10,
      right: 10,
      borderTop: `2px solid ${borderColor}`,
      borderRight: `2px solid ${borderColor}`,
    };
  }

  if (position === 'br') {
    return {
      ...base,
      right: 10,
      bottom: 10,
      borderRight: `2px solid ${borderColor}`,
      borderBottom: `2px solid ${borderColor}`,
    };
  }

  return {
    ...base,
    left: 10,
    bottom: 10,
    borderLeft: `2px solid ${borderColor}`,
    borderBottom: `2px solid ${borderColor}`,
  };
}

export function HudFrame({ title, subtitle, trailing, tone = 'neutral', dense = false, children, style }: HudFrameProps) {
  const accent = resolveHudFrameToneColor(tone);

  // Phase 66 — T2 angular brackets: chamfered corners via clip-path polygon
  const chamfer = dense ? 10 : 14;
  const clipPolygon = `polygon(0 ${chamfer}px, ${chamfer}px 0, calc(100% - ${chamfer}px) 0, 100% ${chamfer}px, 100% calc(100% - ${chamfer}px), calc(100% - ${chamfer}px) 100%, ${chamfer}px 100%, 0 calc(100% - ${chamfer}px))`;

  return (
    <section
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: dense ? 8 : 12,
        color: '#edf7ff',
        background:
          'linear-gradient(180deg, rgba(7, 12, 20, 0.96), rgba(4, 8, 14, 0.92)), linear-gradient(135deg, rgba(98, 221, 255, 0.08), transparent 42%), repeating-linear-gradient(180deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 7px)',
        border: '1px solid rgba(155, 185, 212, 0.20)',
        boxShadow: '0 18px 50px rgba(0, 0, 0, 0.36), inset 0 0 0 1px rgba(255,255,255,0.03)',
        borderRadius: 0,
        clipPath: clipPolygon,
        WebkitClipPath: clipPolygon,
        ...style,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.02), inset 0 0 0 2px ${accent}10`,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${accent}CC 16%, ${accent}88 50%, transparent)`,
          pointerEvents: 'none',
        }}
      />
      <div aria-hidden="true" style={cornerStyle('tl', accent)} />
      <div aria-hidden="true" style={cornerStyle('tr', accent)} />
      <div aria-hidden="true" style={cornerStyle('br', accent)} />
      <div aria-hidden="true" style={cornerStyle('bl', accent)} />

      {(title || subtitle || trailing) && (
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: dense ? 8 : 12,
            marginBottom: dense ? 6 : 10,
            paddingBottom: dense ? 6 : 10,
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            {title ? (
              <div
                style={{
                  fontSize: dense ? 9.5 : 11,
                  letterSpacing: dense ? 1.6 : 2.4,
                  textTransform: 'uppercase',
                  color: accent,
                  fontWeight: 800,
                  lineHeight: 1.1,
                }}
              >
                {title}
              </div>
            ) : null}
            {subtitle ? (
              <div style={{ marginTop: dense ? 2 : 4, fontSize: dense ? 9.5 : 11.5, lineHeight: 1.35, color: 'rgba(223, 237, 247, 0.70)' }}>
                {subtitle}
              </div>
            ) : null}
          </div>
          {trailing ? <div style={{ flexShrink: 0, marginTop: -2 }}>{trailing}</div> : null}
        </header>
      )}
      <div style={{ position: 'relative' }}>{children}</div>
    </section>
  );
}
